<template>
  <div class="container-fluid">
    <div class="loading" v-if="isLoading">Loading&hellip;</div>

    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Comisiones — {{ clerk.name || '...' }}</h1>
          <p class="view-header__subtitle">Detalle de comisiones y liquidaciones del vendedor.</p>
        </div>
        <div class="view-header__actions">
          <button type="button" class="btn view-header__btn" @click="goBack">
            <i class="bi bi-arrow-left"></i>
            Volver
          </button>
          <button type="button" class="btn view-header__btn view-header__btn--primary" @click="openLiquidarModal">
            <i class="bi bi-cash-coin"></i>
            Liquidar por monto
          </button>
        </div>
      </div>
    </div>

    <!-- BALANCE -->
    <div class="stats-card">
      <div class="stats-card__value">{{ formatoDinamicoMoneda(clerk.balance) }}</div>
      <div class="stats-card__label">Balance de comisiones</div>
    </div>

    <!-- TOOLBAR (print + historico) -->
    <div class="d-flex justify-content-between align-items-center mb-3" style="gap: 12px; flex-wrap: wrap;">
      <button type="button" class="btn view-header__btn" @click="printSelected">
        <i class="bi bi-printer"></i>
        Imprimir seleccionados
      </button>
      <label class="toggle-switch">
        <span class="toggle-switch__text">Mostrar histórico</span>
        <input v-model="historical" type="checkbox">
        <span class="toggle-switch__slider"></span>
      </label>
    </div>

    <!-- TABLA -->
    <div class="data-table">
      <vdtnet-table
        ref="table"
        :fields="fields"
        :opts="opts"
        :selectCheckbox="1"
        container-class-name="table-responsive"
        class-name="table mb-0"
        @partialSelect="partialSelect"
        @dischargeOne="dischargeOne"
        @removeOne="removeOne"
      />
    </div>

    <!-- MODAL LIQUIDAR POR MONTO -->
    <transition v-if="showLiquidarModal" name="modal">
      <div class="modal-mask-product">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="form-card__header">
              <h6 class="form-card__title">
                <i class="bi bi-cash-coin"></i>
                Liquidar por monto
              </h6>
              <button type="button" class="form-card__btn" @click="closeLiquidarModal">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>

            <div class="modal-body form-card form-card--bare">
              <div v-if="commissionDescription" class="form-card__field">
                <label class="form-card__label">Comisión seleccionada</label>
                <div class="data-table__primary">{{ commissionDescription }}</div>
              </div>

              <div class="form-card__field">
                <label class="form-card__label" for="liq-fund">Caja de origen</label>
                <q-select v-model="fund" :options="fundOptions" placeholder="Selecciona una caja" />
              </div>

              <div class="form-card__field">
                <label class="form-card__label" for="liq-amount">Monto</label>
                <input id="liq-amount" v-model.number="amount" type="number" min="0" step="0.01" class="form-control" placeholder="0.00">
              </div>
            </div>

            <div class="form-card__footer">
              <button type="button" class="form-card__btn" @click="closeLiquidarModal">Cancelar</button>
              <button
                type="button"
                class="form-card__btn form-card__btn--primary"
                :disabled="!canLiquidar"
                @click="liquidarPorMonto()"
              >
                <i class="bi bi-check-lg"></i>
                Liquidar
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import VdtnetTable from '@/components/global/VdtnetTable.vue';
import formatMoneyMixin from "../../../mixins/formatMoneyMixin";
import formatDateMixin from "../../../mixins/formatDateMixin";
import 'datatables.net-buttons/js/buttons.print.js';
import 'datatables.net-buttons-bs4';
import QSelect from '@/components/shared/QSelect.vue';

export default {
  props: ["salesClerkId"],
  components: {
    VdtnetTable,
    QSelect,
  },
  mixins: [formatMoneyMixin, formatDateMixin],
  data: function () {
    return {
      historical: false,
      isLoading: false,
      showLiquidarModal: false,
      clerk: { id: null, name: null, balance: 0 },
      fund: '',
      funds: [],
      commissionId: null,
      commissionDescription: '',
      amount: 0,
      opts: {
        language: {
          url: '//cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json',
        },
        ajax: {
          url: process.env.VUE_APP_BASE_URL + "/api/salesclerks/getCommissions/" + this.salesClerkId,
          type: 'GET',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: (d) => {
            d.historical = this.historical;
            return d;
          },
        },
        processing: false,
        pageLength: 15,
        searching: false,
        destroy: true,
        ordering: true,
        lengthChange: true,
        serverSide: true,
        autoWidth: false,
        // Buttons inicializados pero NO renderizados en el dom; se disparan
        // desde el botón propio "Imprimir seleccionados" via dataTable.button(0).trigger().
        dom: "tr<'row vdtnet-footer'<'col-sm-12 col-md-5'i><'col-sm-12 col-md-7'pl>>",
        buttons: [
          {
            extend: 'print',
            text: 'Imprimir seleccionados',
            exportOptions: {
              modifier: { selected: true },
              columns: [1, 2, 3, 4, 5, 6, 7],
            },
            customize: function (win) {
              $(win.document.body).find('table').css('align', 'left');
              $(win.document.body).find('table').css('font-size', '12pt');
              $(win.document.body).find('tr').append('<td style="width=40pt;"></td>');
              $(win.document.body).find('td').css('min-width', '100pt');
              $(win.document.body).find('td').css('white-space', 'nowrap');
            },
          },
        ],
        select: { style: "multi" },
        order: [[0, "desc"]],
      },
      fields: {
        created_at: {
          label: "Fecha",
          width: "150px",
          searchable: true,
          render: this.formatoFechaHora,
        },
        client: {
          label: "Cliente",
          render: (data, type) =>
            type === 'display' ? `<span class="data-table__primary">${data}</span>` : data,
        },
        doc: { label: "Motivo" },
        total: {
          label: "Monto",
          width: "120px",
          align: "right",
          render: this.formatoDinamicoMoneda,
        },
        fee_percent: { label: "%", width: "60px", align: "right" },
        fee_total: {
          label: "Comisión",
          width: "120px",
          align: "right",
          render: (data, type) => {
            const formatted = this.formatoDinamicoMoneda(data, type);
            return type === 'display' ? `<span class="data-table__primary">${formatted}</span>` : formatted;
          },
        },
        debt: {
          label: "A rendir",
          width: "120px",
          align: "right",
          render: this.formatoDinamicoMoneda,
        },
        status: {
          label: "Estado",
          width: "120px",
          render: (data, type) => {
            if (type !== 'display') return data;
            if (!data) return '<span class="status-badge status-badge--neutral">-</span>';
            const upper = String(data).toUpperCase();
            let variant = 'neutral';
            if (upper.includes('LIQUID') || upper.includes('DISCHARG')) variant = 'success';
            else if (upper.includes('PARC') || upper.includes('PARTIAL')) variant = 'warning';
            return `<span class="status-badge status-badge--${variant}">${data}</span>`;
          },
        },
        action: {
          isLocal: true,
          label: "Acciones",
          width: "130px",
          align: "right",
          defaultContent: `
            <div class="table-action-group">
              <button data-action="partialSelect" class="table-action-btn" type="button" title="Liquidar parcial">
                <i class="bi bi-arrow-down-right"></i>
              </button>
              <button data-action="dischargeOne" class="table-action-btn" type="button" title="Liquidar">
                <i class="bi bi-check-lg"></i>
              </button>
              <button data-action="removeOne" class="table-action-btn" type="button" title="Eliminar">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          `,
        },
      },
    };
  },
  computed: {
    canLiquidar() {
      return !!(this.fund && this.fund.id && this.amount > 0);
    },
    fundOptions() {
      return this.funds.map(f => ({ value: f, label: f.name }));
    },
  },
  mounted() {
    this.getSalesClerkDetail(this.salesClerkId);
    this.getBalance(this.salesClerkId);
    this.getFunds();
    this.$nextTick(() => {
      const dt = this.$refs.table && this.$refs.table.dataTable;
      if (!dt) return;
      dt.on('preXhr.dt', () => { this.isLoading = true; });
      dt.on('xhr.dt', () => { this.isLoading = false; });
    });
  },
  watch: {
    historical() {
      this.$refs.table.reload();
    },
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'Vendedores' });
    },
    printSelected() {
      const dt = this.$refs.table && this.$refs.table.dataTable;
      if (!dt) return;
      const btn = dt.button(0);
      if (btn) btn.trigger();
    },
    openLiquidarModal() {
      this.commissionId = null;
      this.amount = 0;
      this.commissionDescription = '';
      this.showLiquidarModal = true;
    },
    closeLiquidarModal() {
      this.showLiquidarModal = false;
      this.commissionId = null;
      this.amount = 0;
      this.commissionDescription = '';
    },
    getSalesClerkDetail(salesClerkId) {
      this.$http.get('/api/salesclerks/getSalesclerk/' + salesClerkId)
        .then(response => {
          if (response.data.code === 200) {
            this.clerk.id = response.data.data.id;
            this.clerk.name = response.data.data.name;
          }
        })
        .catch(error => alert(error));
    },
    getBalance(salesClerkId) {
      this.$http.get('/api/salesclerks/getBalance/' + salesClerkId)
        .then(response => {
          if (response.data.success) this.clerk.balance = response.data.data;
        })
        .catch(error => alert(error));
    },
    getFunds() {
      this.$http.get("/api/funds/listSimple").then((response) => {
        this.funds = response.data.data;
      });
    },
    liquidarPorMonto() {
      if (!this.canLiquidar) return;
      this.$http.post("/api/salesclerks/newDischarge", {
        clerk_id: this.salesClerkId,
        amount: this.amount,
        fund_origin: this.fund.id,
        commissionId: this.commissionId,
      }).then((response) => {
        if (response.data.code === 201) {
          this.closeLiquidarModal();
          this.getBalance(this.salesClerkId);
          this.$refs.table.reload();
        }
      });
    },
    dischargeOne(data) {
      if (!this.fund || !this.fund.id) {
        this.openLiquidarModal();
        this.commissionId = data.id;
        this.amount = data.fee_total - data.discharged;
        this.commissionDescription = data.doc;
        return;
      }
      this.$http.post("/api/salesclerks/dischargeOne/" + data.id, {
        fund_origin: this.fund.id,
      }).then((response) => {
        if (response.data.code === 201) {
          this.getBalance(this.salesClerkId);
          this.$refs.table.reload();
        }
      });
    },
    partialSelect(data) {
      this.commissionId = data.id;
      this.amount = data.fee_total - data.discharged;
      this.commissionDescription = data.doc;
      this.showLiquidarModal = true;
    },
    removeOne(data) {
      if (!confirm('¿Está seguro que desea eliminar esta comisión?')) return;
      this.$http.get("/api/salesclerks/deleteCommission/" + data.id).then((response) => {
        if (response.data.code === 201) {
          this.getBalance(this.salesClerkId);
          this.$refs.table.reload();
        }
      });
    },
  },
};
</script>

<style scoped>
.modal-mask-product {
  position: fixed;
  z-index: 1050;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(16, 24, 40, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: hidden;
}

.modal-dialog {
  margin: 0;
  width: 100%;
  max-width: 560px;
}

.modal-content {
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.18);
  overflow: hidden;
}

.modal-body {
  max-height: 70vh;
  overflow-y: auto;
  padding: 20px 24px;
}

.modal-enter,
.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-content,
.modal-leave-active .modal-content {
  transform: scale(1.02);
}
</style>
